// ✅ src/scenes/dashboard/index.jsx

import React, { useState, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import Header from "../../components/Header";
import TicketDetailModal from "./TicketDetailModal";
import TicketTable from "../../components/TicketTable";
import AssigneeDropdown from "../../components/AssigneeDropdown";
import {
  fetchTickets as getTicketsFromApi,
  updateFullTicket,
  fetchTicketById,
} from "../../api/tickets";

// ✅ Centralized base URL with fallback
const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";

const Dashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await getTicketsFromApi();
      setTickets(data);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/me`, {
        credentials: "include",
      });
      const data = await res.json();
      setCurrentUserEmail(data.username || null);
    } catch (error) {
      console.error("Failed to fetch current user:", error);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchCurrentUser();

    const interval = setInterval(() => {
      fetchTickets();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  const handleUpdate = async (updateData, closeModal = false) => {
    try {
      const { id, ticketFields, responseData } = updateData;
      const payload = {
        ...(ticketFields || {}),
        ...(responseData ? { newResponse: responseData } : {}),
      };

      const response = await updateFullTicket(id, payload);
      if (!response || response.error) throw new Error(response?.error || "Unknown error");

      const updatedTicket = await fetchTicketById(id);

      setTickets((prev) => prev.map((t) => (t.id === id ? updatedTicket : t)));

      if (selectedTicket?.id === id) {
        setSelectedTicket(updatedTicket);
      }

      if (closeModal) {
        setSelectedTicket(null);
      }

      // 🔁 Always refresh full table after any update
      fetchTickets();
    } catch (error) {
      console.error("Error updating ticket:", error);
    }
  };

  return (
    <Box m="20px">
      <Header
        title="WEBTEAM TICKETS"
        subtitle="Fixing Sitecore, one ticket at a time"
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TicketTable
          title="Open Tickets"
          tickets={tickets}
          onRowClick={(ticket) => setSelectedTicket(ticket)}
          renderAssignee={(ticket) => (
            <AssigneeDropdown
              ticket={ticket}
              onChange={(val) =>
                handleUpdate(
                  { id: ticket.id, ticketFields: { assigned_to: val } },
                  true
                )
              }
            />
          )}
        />
      )}

      <TicketDetailModal
        open={Boolean(selectedTicket)}
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onUpdate={handleUpdate}
        currentUserEmail={currentUserEmail}
      />
    </Box>
  );
};

export default Dashboard;